import { jsx as _jsx } from "react/jsx-runtime";
import { Assembler, getObjectHash, QueryTypes, sortByName } from '@iotv/datamodel';
import { Autocomplete, CircularProgress, FormControl, InputAdornment } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ApiGateway from '../../data/aws/api-gateway/ApiGateway';
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { ExpressionFunctionType, LogicalOperatorType } from '../../types';
import { getVOBListHash } from '../../util/AppData/viewObject';
import { useDocumentClientQuery } from '../../v2/components/generic/ObjectsDataTable/useDocumentClientQueryV3';
import { AutosizedTextField } from '../common/AutosizedTextField';
const debug = process.env.REACT_APP_DEBUG && false;
const apiRef = 'ConsumerApi';
const noOptions = 'No options available';
const queryingOptions = 'Waiting for results';
const requestSetOption = 'Select from list';
const getQueryParams = ({ supportedTypes, searchText, contextObject, adjacencyType, objectTypeId, }) => {
    // /** to coerce ListLimitedAllTypes query to get a no-context adjacent, a zombie is provided of same type as objectTypeId */
    // if ( !contextObject && objectTypeId && adjacencyType === AdjacentType.SUBJECT ) {
    //     contextObject = getZombieInstanceFromPrimaryKey( `${objectTypeId}:`)
    // }
    const params = {
        ExclusiveStartKey: undefined,
        limit: 25,
        contextObject,
        excludeRelatedToObject: undefined,
        includeEdges: false,
        includeNodes: true,
        adjacencyType,
        objectTypeId: objectTypeId ?? 'never',
        queryType: QueryTypes.LIST_LIMITED_ALL_TYPES,
    };
    if (supportedTypes) {
        params.filterGroups = supportedTypes.map((supportedType) => {
            return {
                setOperation: LogicalOperatorType.OR,
                filters: [{ key: 'supportedType', predicate: ExpressionFunctionType.CONTAINS, value: supportedType }],
            };
        });
    }
    if (searchText) {
        params.filterGroups = [
            {
                setOperation: LogicalOperatorType.AND,
                filters: [{ key: 'name', predicate: ExpressionFunctionType.BEGINS_WITH, value: searchText }],
            },
        ];
    }
    return params;
};
export const SingleObjectAutoSearch = ({ contextObject: contextObjectIn, adjacencyType, objectTypeId: objectTypeIdIn, currentEntity, setCurrentEntity, supportedTypes = [], style, fullWidth, dense = true, disabled = false, required = false, ignoreInitialSearchText = false, }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const initialSearchText = !ignoreInitialSearchText ? (currentEntity?.name ?? '') : '';
    const [searchText, setSearchText] = useState(initialSearchText);
    const [objectTypeId, setObjectTypeId] = useState(undefined);
    const [contextObject, setContextObject] = useState(contextObjectIn);
    const [queryParams, setQueryParams] = useState();
    const [querying, setQuerying] = useState(false);
    debug && console.log(`SingleObjectAutoSearch initialSearchText '${initialSearchText}'`);
    const doTypeUpdate = () => {
        debug && console.log('SingleObjectAutoSearch used effect on objectTypeId, supportedTypes', { objectTypeId, objectTypeIdIn, supportedTypes });
        setSearchText(''); // ensure that any previous search is cleared, as it probably doesn't apply to the new object type
        setObjectTypeId(objectTypeIdIn);
        const queryParams = getQueryParams({ supportedTypes, searchText, contextObject, adjacencyType, objectTypeId: objectTypeIdIn });
        setQueryParams(queryParams);
        // CRA note: due to changes in the underlying useDocumentClientQuery, this leads to flickering to (0) items when querying
        // CRA note: if you encounter issues, feel free it uncomment it again
        // setItems([])
    };
    useEffect(() => {
        debug && console.log('SingleObjectAutoSearch used effect on objectTypeId', { objectTypeId, objectTypeIdIn });
        doTypeUpdate();
    }, [objectTypeIdIn]);
    useEffect(() => {
        debug && console.log('SingleObjectAutoSearch used effect on supportedTypes', { supportedTypes });
        doTypeUpdate();
    }, [supportedTypes]);
    useEffect(() => {
        debug && console.log('SingleObjectAutoSearch used effect on currentEntity', { currentEntity });
    }, [currentEntity?.sk]);
    useEffect(() => {
        debug && console.log('SingleObjectAutoSearch used effect on adjacencyType', { adjacencyType });
    }, [adjacencyType]);
    const debouncerRef = useRef(undefined);
    const handleInputChange = (e) => {
        const target = e.target;
        const searchText = target.value;
        const queryParams = getQueryParams({ supportedTypes, searchText, contextObject, adjacencyType, objectTypeId });
        debug && console.log(`SingleObjectAutoSearch queryParamas`, queryParams);
        if (debouncerRef.current === undefined) {
            setQueryParams(queryParams);
            setSearchText(searchText);
        }
        else {
            clearTimeout(debouncerRef.current);
        }
        debouncerRef.current = setTimeout(() => {
            // setCurrentEntity( undefined )
            setQueryParams(queryParams);
            setSearchText(searchText);
            delete debouncerRef.current;
        }, 750);
    };
    const query = async (contextRequest) => {
        let res = { err: null, data: null };
        if (contextRequest.objectTypeId) {
            debug && console.log('SingleObjectAutoSearch query request', contextRequest);
            try {
                setQuerying(true);
                const response = await ApiGateway.post(`/query/`, contextRequest, apiRef);
                if (response.data?.Items) {
                    response.data.Items = response.data.Items.map((zombie) => Assembler.getInstance(zombie)).sort(sortByName);
                }
                debug && console.log('SingleObjectAutoSearch query results', response);
                res = response;
            }
            catch (e) {
                debug && console.log('Error in UM query', e);
                setQuerying(false);
            }
        }
        else {
            debug && console.log('SingleObjectAutoSearch ignored query request', contextRequest);
            setQuerying(false);
        }
        return res;
    };
    const queryHook = useDocumentClientQuery({ queryParams, query, dependencies: [getObjectHash(queryParams)], limit: 25 });
    const [[items, setItems], [limit, setLimit], [LastEvaluatedKeys, setLastEvaluatedKeys], [ExclusiveStartKey, setExclusiveStartKey]] = queryHook.querySetterGetters;
    const notifiableEntitiesHash = getVOBListHash(items);
    const [listItems, setListItems] = useState(currentEntity ? [currentEntity, ...items] : [...items]);
    useEffect(() => {
        debug && console.log('SingleObjectAutoSearch Used effect on VOB Hash', notifiableEntitiesHash);
    }, [notifiableEntitiesHash]);
    debug && console.log('SingleObjectAutoSearch objectTypeId', objectTypeId);
    useEffect(() => {
        if (queryHook.inFlight)
            return;
        setListItems([...items]);
        if (currentEntity && !items.find((item) => item.sk === currentEntity.sk)) {
            setListItems((i) => [currentEntity, ...i]);
        }
    }, [items]);
    const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
    useEffect(() => {
        if (queryHook.inFlight)
            return;
        setAutoCompleteOptions(listItems.map((item) => item.sk));
    }, [listItems]);
    useEffect(() => {
        if (autoCompleteOptions)
            setQuerying(false);
    }, [autoCompleteOptions]);
    const autoCompleteLabel = `${pluralReadableTypeName(objectTypeId)} (${querying ? '...' : items.length}) ${LastEvaluatedKeys ? '...and more' : ''}`;
    const InputProps = querying
        ? {
            ['InputProps']: {
                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { color: "inherit", size: 18 }) })),
            },
        }
        : undefined;
    const componentMap = {
        entity: (_jsx(FormControl, { fullWidth: fullWidth, children: _jsx(Autocomplete, { value: currentEntity?.sk ?? '',
                id: 'selectEntity',
                style,
                size: 'small',
                disabled,
                fullWidth,
                disableClearable: true,
                forcePopupIcon: querying ? false : 'auto',
                loading: querying,
                options: autoCompleteOptions,
                filterOptions: (x) => x, // disable built-in filtering
                renderOption: (props, option) => {
                    const { key, ...rest } = props;
                    return (_jsx("li", { ...rest, children: listItems.find((item) => item.sk === option)?.name ?? '' }, key));
                },
                isOptionEqualToValue: (option, value) => (value.sk ? option === value.sk : option === value),
                onChange: (e, v) => {
                    const matchedItem = listItems.find((item) => item.sk === v);
                    if (!matchedItem) {
                        /** user has selected Select from List  */
                        debug && console.log('SingleObjectAutoSearch user has selected Select from List', { searchText });
                        setCurrentEntity(undefined);
                    }
                    setCurrentEntity(matchedItem);
                },
                renderInput: (params) => {
                    const { inputProps } = params;
                    // @ts-expect-error `string | null` is not assignable to `string | undefined`
                    inputProps.value = currentEntity?.name ?? null;
                    return (_jsx(AutosizedTextField, { ...params,
                        ...InputProps,
                        label: autoCompleteLabel,
                        placeholder: querying ? queryingOptions : requestSetOption,
                        onChange: handleInputChange,
                        disabled,
                        error: required && !currentEntity }));
                } }) })),
    };
    return componentMap.entity;
};
/** Like a single object auto search but takes a branching query def that is run against a provided context object */
export const SingleObjectAutoQuery = ({ query, contextObject, objectTypeId, labelOverride, filter, selectedObject, changeSelectedObject, disabled, required = false, }) => {
    const [options, setOptions] = useState([]);
    const [querying, setQuerying] = useState(false);
    const queryHash = getObjectHash(query);
    useEffect(() => {
        setOptions([]);
        setQuerying(true);
        ConsumerDao.getGraphQueryRequestV2({ query, contextObjects: [contextObject] }).then((res) => {
            if (res.err || !res.data) {
                setQuerying(false);
                console.error('Error in SingleObjectAutoQuery query', res);
                return;
            }
            const opts = res.data
                .getContainedItems()
                .filter((n) => n.type === objectTypeId)
                .filter(filter ?? ((x) => x));
            setOptions(opts);
            setQuerying(false);
        });
    }, [queryHash, contextObject.sk]);
    const autoCompleteLabel = `${labelOverride ?? readableTypeName(objectTypeId)} (${querying ? '...' : options.length})`;
    return (_jsx(FormControl, { fullWidth: true, children: _jsx(Autocomplete, { options: options, renderInput: (p) => (_jsx(AutosizedTextField, { ...p,
                placeholder: querying ? queryingOptions : requestSetOption,
                label: autoCompleteLabel,
                error: required && !selectedObject })), getOptionLabel: (o) => o.name ?? o.sk, isOptionEqualToValue: (o, v) => o.sk === v.sk, multiple: false, loading: querying, value: selectedObject, onChange: (_, opt) => changeSelectedObject?.(opt), disabled: disabled, fullWidth: true, disableClearable: true, forcePopupIcon: querying ? false : 'auto', size: 'small' }) }));
};
